import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { BetBuilderConfig, BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SmoothScroll } from '@frontend/sports/common/core/feature/smooth-scroll';
import { IPayloadAction, noopAction } from '@frontend/sports/common/core/utils/redux';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ButtonType } from '@frontend/sports/modal/feature';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { NativeAppService, WINDOW } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { groupBy } from 'lodash-es';
import { LDFeatureFlags, LaunchDarklyFlagManagerService } from 'packages/sports/web/app/src/bootstrap/launch-darkly-flag-manager.service';
import { PrecreatedBabTrackingService } from 'packages/sports/web/app/src/option-group/precreated-bab-tracking.service';
import { IUiRootState, IUiState, uiStateSelector } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { concatMap, delay, filter, finalize, first, from, ignoreElements, map, mergeMap, of, startWith, switchMap, take, tap, timer } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipGroupInformation } from '../../../core/groups/betslip-group-information';
import { BetslipBetBuilderPick, SportcastOption } from '../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick, BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { GroupEnabledPickIds, GroupPickId, PickId, V2OptionMarketPickId, isGroupEnabledPickId } from '../../../core/picks/pick-id';
import { IPickTracking, PriceType } from '../../../core/picks/pick-models';
import { BetslipV2HorseRaceOptionMarketPick } from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { getPicksCount } from '../../../core/utils';
import { OverAskFlowState } from '../../../model/over-ask/over-ask';
import BetbuilderActions from '../../bet-builder/actions';
import { BetBuilderDialogService } from '../../bet-builder/services/bet-builder-dialog.service';
import { BetBuilderUtilService } from '../../bet-builder/services/bet-builder-util.service';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import { betslipPlaceButtonStateSelector } from '../../betplacement/selectors';
import { PlaceButtonMode } from '../../betplacement/state';
import { EditBetActions } from '../../edit-bet/actions';
import { ensureLinearMode } from '../../linear/helpers';
import { OverAskMessagesProvider } from '../../over-ask/services/over-ask-messages.provider';
import { QuickBetUiState } from '../../quick-bet/quick-bet.state';
import TrackingActions from '../../tracking/actions';
import { trackingLdActiveExperimentsSelector } from '../../tracking/selectors';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import { betslipSelectedTypeStateSelector } from '../../types/selectors';
import { IBetslipTypeState } from '../../types/state';
import BetslipErrorsActions from '../../validation/actions';
import BetslipPickActions from '../actions';
import BetslipPickFactory from '../factory/betslip-pick.factory';
import { removePicksFromGroup, requestAddGroup, requestAddPickToGroup, requestNavigateToBABTab } from '../grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../helpers';
import { betslipPicksListSelector } from '../selectors';
import BetslipPickUtilsService from '../services/betslip-pick-utils.service';

@Injectable()
export default class AddRemoveEffects {
    private readonly _doc = inject(DOCUMENT);
    private static readonly defaultHighlightDelayMs = 4000;
    private requestAddActions = [
        BetslipPickActions.requestAddPick,
        BetslipPickActions.requestAddBetBuilderPick,
        BetslipPickActions.requestAddEntainUiBetBuilderPick,
        BetslipPickActions.requestReplacePreCreatedBetBuilderPick,
    ];

    private requestRemoveActions = [BetslipPickActions.requestRemovePick, BetslipPickActions.requestRemoveEntainUiBetBuilderPick];
    private readonly _window = inject(WINDOW);

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private storeUi: Store<IUiRootState>,
        private pickFactory: BetslipPickFactory,
        private toaster: ToasterService,
        private overAskMessageProvider: OverAskMessagesProvider,
        private timerService: TimerService,
        private smoothScrollService: SmoothScroll,
        private betslipUtilsService: BetslipPickUtilsService,
        private betBuilderConfig: BetBuilderConfig,
        private sitecore: Sitecore,
        private betBuilderUtilService: BetBuilderUtilService,
        private betBuilderDialogService: BetBuilderDialogService,
        private precreatedTrackingService: PrecreatedBabTrackingService,
        private nativeAppService: NativeAppService,
        private betslipConfig: BetslipConfig,
        private ldFlagManagerService: LaunchDarklyFlagManagerService,
    ) {}

    private isEditBetMode(betslipState: IBetslipState) {
        return betslipState.types.base.currentSelectedType === BetslipType.EditBet;
    }

    private isEditBetAndNotResult(betslipState: IBetslipState) {
        return this.isEditBetMode(betslipState) && betslipState.base.state !== BetslipState.Result;
    }

    private isInOveraskFlow(betslipState: IBetslipState) {
        return betslipState.overAskState.flowState !== OverAskFlowState.None;
    }

    private isNotInPlacingState(betslipState: IBetslipState) {
        return betslipState.base.state !== BetslipState.Placing;
    }

    private isOverMaxPickCount(betslipState: IBetslipState) {
        return getPicksCount(betslipState.picks.pickList) >= this.betslipConfig.maxBetslipBets;
    }

    private allowGenericAddEffects(betslipState: IBetslipState) {
        return (
            this.isNotInPlacingState(betslipState) &&
            !this.isEditBetAndNotResult(betslipState) &&
            !this.isInOveraskFlow(betslipState) &&
            !this.isOverMaxPickCount(betslipState)
        );
    }

    private hasBetGroupFor(groupInfo: BetslipGroupInformation, betslipState: IBetslipState): boolean {
        if (!groupInfo?.groupId) {
            return false;
        }

        //should have angstrom or byb football enabled and an existing pick.
        return betslipState.picks.pickList.some((t) => !!t.groupInfo && t.groupInfo.groupId === groupInfo.groupId);
    }

    private shouldAutoSetCombo(betslipState: IBetslipState) {
        if (betslipState.types.base.currentSelectedType !== BetslipType.Combo) {
            if (betslipState.types.base.selectedByUser) {
                return false;
            }
            if (betslipState.picks.pickList.length > 1) {
                return false;
            }

            return true;
        }

        return false;
    }

    handleRequestAddPickEditBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...this.requestAddActions),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => this.isEditBetAndNotResult(betslipState)),
            map(([action, _]) => EditBetActions.startAddingPickToContainer({ pickId: action.pickId })),
        );
    });

    handleRequestAddPickLaunchDarklyDisplayMode$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(...this.requestAddActions),
                concatLatestFrom(() => this.store.select(betslipSelectedTypeStateSelector)),
                concatLatestFrom(() => this.store.select(trackingLdActiveExperimentsSelector)),
                filter(([[_, type], ldExperiments]) => type !== BetslipType.EditBet && !ldExperiments[LDFeatureFlags.DisplayMode]),
                switchMap(() => this.ldFlagManagerService.activateExperiment(LDFeatureFlags.DisplayMode)),
                first(),
                switchMap(() => of(TrackingActions.setLaunchDarklyActiveExperiment({ id: LDFeatureFlags.DisplayMode }))),
                ignoreElements(),
            );
        },
        { dispatch: false },
    );

    handleRequestAddPickToastrForOverask$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(...this.requestAddActions, ...this.requestRemoveActions),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                filter(([_, betslipState]) => !this.isEditBetAndNotResult(betslipState)),
                filter(([_, betslipState]) => this.isInOveraskFlow(betslipState)),
                tap((_) =>
                    this.toaster.open(this.overAskMessageProvider.messages.betslipLockedToastMessage, {
                        panelClass: 'overask-toast-message betslip-vars-scope',
                    }),
                ),
                ignoreElementsTyped(),
            );
        },
        {
            dispatch: false,
        },
    );

    // Don't add the pick when the betslip is full.
    handleMaxPickListCountOnAddRequest$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...this.requestAddActions),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => !this.isEditBetAndNotResult(betslipState)),
            filter(([_, betslipState]) => !this.isInOveraskFlow(betslipState)),
            filter(([_, betslipState]) => this.isOverMaxPickCount(betslipState)),
            map((_) => {
                if (
                    !this.nativeAppService.isTerminal ||
                    ['bottom-screen', 'betstationscreen2', 'racing-bottom-screen'].includes(this.nativeAppService.applicationName)
                ) {
                    return BetslipErrorsActions.showBetslipFullDialog();
                }

                return noopAction;
            }),
        );
    });

    handleRequestAddEntainPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestAddEntainUiBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => this.allowGenericAddEffects(betslipState)),
            map(([action, betslipState]) => {
                const existingBetBuilderPickFromEntainUi = betslipState.picks.pickList.find(
                    (p) => BetslipBetBuilderPick.isEntainUiBetBuilderPick(p) && p.eventId === action.eventData.event.id,
                ) as BetslipBetBuilderPick | undefined;

                if (!existingBetBuilderPickFromEntainUi || action.isPrecreatedBAB) {
                    let sportcastOptions: SportcastOption[];
                    if (action.isPrecreatedBAB) {
                        sportcastOptions = action.market.name.split(';').reduce((options: SportcastOption[], name: string, index) => {
                            options.push({ longId: action.longId[index], name });

                            return options;
                        }, []);
                    } else {
                        sportcastOptions = [
                            {
                                longId: action.longId[0],
                                name: action.market.name,
                            },
                        ];
                    }

                    const pick = this.pickFactory.createEntainUiBetBuilderPick(
                        action.pickId,
                        action.eventData,
                        action.eventModel,
                        action.market,
                        action.tracking,
                        sportcastOptions,
                    );

                    return BetslipPickActions.addEntainUiBetBuilderPick({
                        pick,
                        isPrecreatedBAB: !!action.isPrecreatedBAB,
                        headerTemplate: action.headerTemplate,
                    });
                } else {
                    const sportcastOptions = [...existingBetBuilderPickFromEntainUi.sportcastOptions];

                    const maxSelectionsPerPick = this.betBuilderConfig.maxSelectionsPerPick;

                    if (sportcastOptions.length >= maxSelectionsPerPick) {
                        const message = interpolateDoubleBracesString(this.sitecore.betBuilderEntainUI.messages?.PickLimitErrorMessage, {
                            count: maxSelectionsPerPick,
                        });

                        return BetbuilderActions.displayErrorMessage({ message });
                    }

                    sportcastOptions.push({ name: action.market.name, longId: action.longId[0] });

                    const stake = this.getPickStake(betslipState.types, existingBetBuilderPickFromEntainUi.id.toString());

                    return BetbuilderActions.requestBetBuilderPrice({
                        payload: {
                            betgeniusId: Number(action.eventModel.getBetBuilderConsumerFixtureId()),
                            eventId: action.eventModel.id,
                            externalFixtureId: action.eventModel.sportcastId!,
                            pickId: action.pickId,
                            sportId: action.eventModel.sport.id,
                            stake,
                            sportcastOptions,
                            useV2Key: !!action.eventModel.betBuilderTradingV2FixtureId,
                        },
                    });
                }
            }),
        );
    });

    handleAddEntainUiBetBuilderPickForAddPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addEntainUiBetBuilderPick),
            map(({ pick }) => BetslipPickActions.addPick({ pick })),
        );
    });

    handleAddEntainUiBetBuilderPickForReplacePick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addEntainUiBetBuilderPick),
            mergeMap((addEntainUiBetBuilderPickAction) =>
                this.actions$.pipe(
                    ofType(BetslipPickActions.addPick),
                    filter((addPickAction) => addPickAction.pick === addEntainUiBetBuilderPickAction.pick),
                    take(1),
                ),
            ),
            map(({ pick }) => BetslipPickActions.replacePick({ pickId: pick.id, newPick: pick, newPickAdded: true })),
        );
    });

    handleAddEntainUiBetBuilderPickForNavigateToBABTab$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.addEntainUiBetBuilderPick),
                filter((action) => action.isPrecreatedBAB),
                mergeMap((addEntainUiBetBuilderPickAction) =>
                    this.actions$.pipe(
                        ofTypeReplaceExistingPick(),
                        filter((replacePickAction) => replacePickAction.newPick === addEntainUiBetBuilderPickAction.pick),
                        map((_) => addEntainUiBetBuilderPickAction),
                        take(1),
                    ),
                ),
                tap((action) => this.betBuilderUtilService.navigateToBABTab(action.pick, action.headerTemplate!)),
            );
        },
        { dispatch: false },
    );

    handleRequestAddPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestAddPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => this.allowGenericAddEffects(betslipState)),
            concatLatestFrom(() => this.storeUi.select(uiStateSelector)),
            mergeMap(([[action, betslipState], uiState]) => {
                const { priceId } = action;
                const existing = betslipState.picks.pickList.find((p) => p.id.isEqual(action.pickId));
                if (existing && !BetslipUnknownPick.isPick(existing) && priceId && priceId !== existing.currentPrice?.id) {
                    const newPrice = existing.prices.find((price) => price.id === priceId);
                    if (newPrice) {
                        switch (newPrice.type) {
                            case PriceType.Fixed:
                                return of(BetslipPickActions.setFixedPrice({ pickId: action.pickId }));
                            case PriceType.StartingPrice:
                                return of(BetslipPickActions.setStartingPrice({ pickId: action.pickId }));
                            default:
                                break;
                        }
                    }
                }

                //don't propagate the normal path, instead, request adding the group.
                if (
                    // This should not trigger on result state because the placed picks have not been cleared yet
                    betslipState.base.state !== BetslipState.Result &&
                    betslipState.quickBet.uiState !== QuickBetUiState.Success &&
                    !!action.groupInfo &&
                    isGroupEnabledPickId(action.pickId) &&
                    this.hasBetGroupFor(action.groupInfo, betslipState)
                ) {
                    const requestAddAction = requestAddPickToGroup({
                        ...action,
                        groupInfo: action.groupInfo,
                        pickId: action.pickId,
                    });
                    if (betslipState.types.base.currentSelectedType === BetslipType.Combo) {
                        return of(requestAddAction);
                    }
                    //it's not combo but we can auto select combo, propagate combo set + request add pick
                    if (this.shouldAutoSetCombo(betslipState)) {
                        return from([
                            BetslipTypesActions.setSelectedType({
                                betslipType: BetslipType.Combo,
                                selectedByUser: false,
                                systemInfo: null,
                            }),
                            requestAddAction,
                        ]);
                    }
                }

                const { isPriceBoosted } = action;

                return this.betslipUtilsService.loadPickFromApi(action.pickId).pipe(
                    map((enhancedFixture) =>
                        this.pickFactory.createPick(
                            action.pickId,
                            action.tracking,
                            enhancedFixture.fixture,
                            priceId,
                            isPriceBoosted,
                            undefined,
                            action.groupInfo,
                        ),
                    ),
                    map((pick) => {
                        if (!pick) {
                            return BetslipPickActions.removePick({ pickId: action.pickId });
                        }

                        if (pick.highlighted) {
                            this.scrollToSummary(uiState);
                        }

                        return BetslipPickActions.replacePick({
                            pickId: action.pickId,
                            newPick: pick,
                            newPickAdded: true,
                        });
                    }),
                    startWith(BetslipPickActions.addPick({ pick: new BetslipUnknownPick(action.pickId, action.tracking) })),
                );
            }),
        );
    });

    handleRequestAddGroupPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestAddGroupPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => this.allowGenericAddEffects(betslipState)),
            switchMap(([{ payload, headerTemplate }, betslipState]) => {
                const existingPicks: BetslipPick[] = [];
                betslipState.picks.pickList.forEach((pick) => {
                    if (GroupPickId.isId(pick.id)) {
                        existingPicks.push(...(pick as BetslipGroupPick).getLegs());
                    } else {
                        existingPicks.push(pick);
                    }
                });
                const picksToAdd = payload.filter((pick) => !existingPicks.some((p) => p.id.isEqual(pick.pickId)));

                if (
                    betslipState.types.base.currentSelectedType !== BetslipType.Combo &&
                    betslipState.picks.pickList.length > 1 &&
                    betslipState.base.state !== BetslipState.Result
                ) {
                    return of(
                        ...picksToAdd.map((y) =>
                            BetslipPickActions.requestAddPick({
                                pickId: y.pickId,
                                tracking: y.tracking,
                                priceId: y.priceId,
                                isPriceBoosted: y.isPriceBoosted,
                                groupInfo: y.groupInfo,
                                parentLinkedEventId: y.parentLinkedEventId,
                                isBetBuilderEnabled: true,
                            }),
                        ),
                    );
                }

                if (!picksToAdd.length && headerTemplate) {
                    const existingGroupPick = betslipState.picks.pickList.find((t) => t.groupInfo?.groupId === existingPicks[0].groupInfo?.groupId)!;

                    return of(requestNavigateToBABTab({ pick: existingGroupPick, headerTemplate }));
                }

                return of(
                    requestAddGroup({
                        picksIds: picksToAdd.flatMap((p) => p.pickId as GroupEnabledPickIds),
                        tracking: picksToAdd[0].tracking,
                        groupInfo: picksToAdd[0].groupInfo!,
                        isBetBuilderEnabled: true,
                        headerTemplate,
                    }),
                ).pipe(
                    startWith(
                        BetslipTypesActions.setSelectedType({
                            betslipType: BetslipType.Combo,
                            selectedByUser: false,
                            systemInfo: null,
                        }),
                    ),
                );
            }),
        );
    });

    handleRequestAddBetBuilderPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestAddBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => this.allowGenericAddEffects(betslipState)),
            switchMap(([action, _]) => {
                return this.betslipUtilsService.loadPickFromApi(action.pickId).pipe(
                    map((enhancedFixture) =>
                        this.pickFactory.createBetBuilderPick(
                            action.pickId,
                            action.tracking,
                            enhancedFixture.fixture,
                            action.eventData,
                            undefined,
                            action.additionalInfo,
                        ),
                    ),
                    map((pick) => {
                        if (!pick) {
                            return BetslipPickActions.removePick({ pickId: action.pickId });
                        }

                        return BetslipPickActions.replacePick({
                            pickId: action.pickId,
                            newPick: pick,
                            newPickAdded: true,
                        });
                    }),
                    startWith(BetslipPickActions.addPick({ pick: new BetslipUnknownPick(action.pickId, action.tracking) })),
                );
            }),
        );
    });

    private scrollToSummary(uiState: IUiState): void {
        const hasColumn = uiState.states.some((s) => s === UiStates.RightColumnVisible);
        if (hasColumn) {
            this.timerService.setTimeoutOutsideAngular(() => {
                this.scrollColumnRightToSummary(this.smoothScrollService);
            }, 0);
        } else {
            this.timerService.setTimeoutOutsideAngular(this.scrollToBottom, 300);
        }
    }

    private scrollToBottom(): void {
        const maximumScrolling = 10000;

        const betslipContainer = this._doc.querySelector('.betslip-edit-state__scroller');

        if (betslipContainer) {
            betslipContainer.scrollTop = maximumScrolling;
        }
    }

    private scrollColumnRightToSummary(smoothScrollService: SmoothScroll): void {
        const columnRightElement = this._doc.querySelector('.column-right .column-view');
        const betslipEditBetSummaryContainerElement = this._doc.querySelector('.betslip-summary');

        if (columnRightElement && betslipEditBetSummaryContainerElement) {
            const height =
                columnRightElement.scrollTop + betslipEditBetSummaryContainerElement.getBoundingClientRect().bottom - this._window.innerHeight;
            smoothScrollService.scrollTopTo(columnRightElement as HTMLElement, height, 350, true);
        }
    }

    handleRequestRemoveEntainBetBuilderPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemoveEntainUiBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => !this.isInOveraskFlow(betslipState) && this.isNotInPlacingState(betslipState)),
            map(([action, betslipState]) => {
                const existingBetBuilderPickFromEntainUi = betslipState.picks.pickList.find(
                    (p) => BetslipBetBuilderPick.isEntainUiBetBuilderPick(p) && p.eventId === action.eventId,
                ) as BetslipBetBuilderPick;

                existingBetBuilderPickFromEntainUi.sportcastOptions = existingBetBuilderPickFromEntainUi.sportcastOptions.filter(
                    (option) => option.longId !== action.longId,
                );

                if (!existingBetBuilderPickFromEntainUi.sportcastOptions.length) {
                    return BetslipPickActions.removePick({ pickId: existingBetBuilderPickFromEntainUi.id });
                }

                const stake = this.getPickStake(betslipState.types, existingBetBuilderPickFromEntainUi.id.toString());

                return BetbuilderActions.requestBetBuilderPrice({
                    payload: {
                        betgeniusId: Number(existingBetBuilderPickFromEntainUi.betgeniusId),
                        eventId: existingBetBuilderPickFromEntainUi.eventId,
                        externalFixtureId: existingBetBuilderPickFromEntainUi.sportcastId!,
                        pickId: action.pickId,
                        sportId: existingBetBuilderPickFromEntainUi.sportId,
                        stake,
                        sportcastOptions: existingBetBuilderPickFromEntainUi.sportcastOptions,
                        useV2Key: !!existingBetBuilderPickFromEntainUi.useV2Key,
                    },
                });
            }),
        );
    });

    handleRequestRemovePick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemovePick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => !this.isInOveraskFlow(betslipState) && this.isNotInPlacingState(betslipState)),
            filter(([_, betslipState]) => !this.isEditBetMode(betslipState)),
            concatMap(([action, betslipState]) => {
                if (isGroupEnabledPickId(action.pickId)) {
                    const parentGroup = betslipState.picks.pickList.find(
                        (p) => BaseBetslipGroupPick.isPick(p) && p.hasPick(action.pickId as GroupEnabledPickIds),
                    );
                    if (parentGroup) {
                        return [
                            removePicksFromGroup({
                                groupInfo: parentGroup.groupInfo!,
                                pickIds: [action.pickId],
                            }),
                        ];
                    }
                }
                const pick = betslipState.picks.pickList.find((p) => p.id.toString() === action.pickId.toString());

                if (!pick) {
                    return [];
                }

                const childrenIds: PickId[] = [];
                if (BaseBetslipGroupPick.isPick(pick)) {
                    childrenIds.push(...pick.getLegs().map((p) => p.id));
                }

                return [
                    BetslipPickActions.removePick({
                        pickId: action.pickId,
                        eventName: action.eventName,
                        groupInfo: pick.groupInfo,
                        childrenPickIds: childrenIds,
                    }),
                ];
            }),
        );
    });

    handleRequestRemovePickEditBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemovePick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => !this.isInOveraskFlow(betslipState) && this.isNotInPlacingState(betslipState)),
            filter(([_, betslipState]) => this.isEditBetMode(betslipState)),
            map(([action, betslipState]) => {
                if (betslipState.types.editBet.originalPicks.includes(action.pickId.toString())) {
                    return EditBetActions.removePick({ pickId: action.pickId.toString() });
                } else {
                    return EditBetActions.removeFromContainer({ pickId: action.pickId.toString() });
                }
            }),
        );
    });

    handleRequestRemoveAllPicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemoveAllPicks),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            map(([_, pickList]) => pickList.map((pick) => pick.id)),
            map((pickIds) => BetslipPickActions.requestRemoveMultiplePicks({ pickIds })),
        );
    });

    handleRequestRemoveMultiplePicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemoveMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            map(([action, betslipState]) => {
                const actionPickIds = groupBy(action.pickIds, (p) => p.toString());

                const groupPicks = betslipState.picks.pickList
                    .filter(BaseBetslipGroupPick.isPick)
                    .filter((pick) => actionPickIds[pick.id.toString()]);

                const childrenIds = groupPicks.flatMap((gp) => gp.getLegs().map((p) => p.id));

                return BetslipPickActions.removeMultiplePicks({ pickIds: [...action.pickIds, ...childrenIds] });
            }),
        );
    });

    handleRequestRemovePlacedChildPicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemovePlacedPicksFromGroup),
            ensureLinearMode(this.store),
            concatLatestFrom(() => [this.store.select(betslipPicksListSelector)]),
            concatMap(([action, picks]) => {
                const pickIds = action.pickIds;

                const groups = pickIds.reduce(
                    (acc, id) => {
                        if (GroupPickId.isId(id)) {
                            return acc;
                        }

                        const parentPick = picks.find((p) => BaseBetslipGroupPick.isPick(p) && p.hasPick(id as GroupEnabledPickIds));

                        const parentGroup = parentPick?.groupInfo;

                        if (!parentPick || !parentGroup || pickIds.some((ids) => ids.isEqual(parentPick.id))) {
                            return acc;
                        }

                        const existingGroup = acc.find((group) => group.groupInfo.groupId === parentGroup.groupId);

                        if (!existingGroup) {
                            return [
                                ...acc,
                                {
                                    groupInfo: parentGroup,
                                    pickIds: [id as GroupEnabledPickIds],
                                },
                            ];
                        }

                        existingGroup.pickIds.push(id as GroupEnabledPickIds);

                        return acc;
                    },
                    [] as {
                        groupInfo: BetslipGroupInformation;
                        pickIds: GroupEnabledPickIds[];
                    }[],
                );

                return groups.map((group) => removePicksFromGroup(group));
            }),
        );
    });

    handleRequestRemovePicksFromGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestRemovePicksFromGroup),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => this.allowGenericAddEffects(betslipState)),
            delay(0),
            map(([action, betslipState]) => {
                if (betslipState.types.base.currentSelectedType !== BetslipType.Combo && betslipState.picks.pickList.length > 1) {
                    return BetslipPickActions.requestRemoveMultiplePicks({ pickIds: action.pickIds });
                }

                const parentGroup = betslipState.picks.pickList.find(
                    (p) => BaseBetslipGroupPick.isPick(p) && p.hasPick(action.pickIds[0] as GroupEnabledPickIds),
                );

                return removePicksFromGroup({
                    pickIds: action.pickIds as GroupEnabledPickIds[],
                    groupInfo: parentGroup?.groupInfo!,
                });
            }),
        );
    });

    handlePicksHighlightEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addPicksHighlight),
            concatMap((action) => {
                if (action.payload.autoRestore) {
                    return timer(AddRemoveEffects.defaultHighlightDelayMs).pipe(
                        take(1),
                        map(() => {
                            return BetslipPickActions.removePicksHighlight({ pickIds: action.payload.pickIds });
                        }),
                    );
                }

                return [] as IPayloadAction<string[]>[];
            }),
        );
    });

    resetPlaceButtonStateOnPicksChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...this.requestAddActions, ...this.requestRemoveActions),
            concatLatestFrom(() => this.store.select(betslipPlaceButtonStateSelector)),
            filter(([_, placeButton]) => placeButton.mode === PlaceButtonMode.Confirm),
            map((_) => BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Normal })),
        );
    });

    handleCheckPrecreatedBABConversionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.checkPrecreatedBABConversion),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            mergeMap(([action, picks]) => {
                const getPickAddAction = () => {
                    if (action.isBetslip) {
                        return BetslipPickActions.requestReplacePreCreatedBetBuilderPick({
                            pickId: action.pickId,
                            headerTemplate: action.headerTemplate,
                            tracking: <IPickTracking>{},
                        });
                    }

                    return BetslipPickActions.requestAddEntainUiBetBuilderPick({
                        pickId: action.pickId,
                        tracking: <IPickTracking>{},
                        eventData: action.eventData!,
                        market: action.market!,
                        eventModel: action.eventModel!,
                        longId: action.longId!,
                        headerTemplate: action.headerTemplate,
                        isPrecreatedBAB: true,
                    });
                };

                const customizedBAB = picks.find(
                    (pick) =>
                        BetslipBetBuilderPick.isEntainUiBetBuilderPick(pick) &&
                        (pick.eventId === action.pickId.eventId || pick.eventId === action.pickId.fixtureId),
                ) as BetslipBetBuilderPick;

                if (customizedBAB) {
                    this.precreatedTrackingService.trackOverlayLoad(
                        customizedBAB.fixture.name.name,
                        customizedBAB.fixture.sportId,
                        customizedBAB.fixture.league?.id!,
                    );

                    return from(this.betBuilderDialogService.showDialog()).pipe(
                        concatMap((result) => {
                            if (result === ButtonType.Ok) {
                                this.precreatedTrackingService.trackAddorEditClick(
                                    customizedBAB.fixture.name.name,
                                    customizedBAB.fixture.sportId,
                                    customizedBAB.fixture.league?.id!,
                                    this.sitecore.betBuilderEntainUI.messages?.EditBABOverlayOk ?? '',
                                    true,
                                );

                                return [BetslipPickActions.requestRemovePick({ pickId: customizedBAB.id }), getPickAddAction()];
                            }
                            this.precreatedTrackingService.trackAddorEditClick(
                                customizedBAB.fixture.name.name,
                                customizedBAB.fixture.sportId,
                                customizedBAB.fixture.league?.id!,
                                this.sitecore.betBuilderEntainUI.messages?.EditBABOverlayCancel ?? '',
                                true,
                            );

                            return [];
                        }),
                    );
                }

                return [getPickAddAction()];
            }),
        );
    });

    handleToggleWinPicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.toggleWinPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            mergeMap(([action, betslipState]) => {
                const pick = betslipState.picks.pickList.find((p) => p.id.isEqual(action.pickId));
                if (pick && pick instanceof BetslipV2HorseRaceOptionMarketPick && pick.siblingOption !== undefined) {
                    const newOption = pick.siblingOption;
                    const newPickId = new V2OptionMarketPickId(pick.id.fixtureId, pick.market.id, newOption.id, newOption.prices[0].id);
                    const newPick = pick.copy();
                    newPick.id = newPickId!;
                    newPick.option = newOption;
                    newPick.siblingOption = pick.option;
                    newPick.priceType = newOption.prices[0].type;
                    newPick.pickType = pick.pickType;
                    newPick.betTypeAltTranslation = pick.betTypeAltTranslation;
                    newPick.acceptedPrice.price = newOption.prices[0];
                    newPick.betType = pick.betType;

                    return [BetslipPickActions.replacePick({ pickId: pick.id, newPick, newPickAdded: true })];
                } else {
                    return [];
                }
            }),
        );
    });

    handleClosingDialog$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(...this.requestAddActions),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                filter(([_, betslipState]) => this.allowGenericAddEffects(betslipState)),
                tap((_) => this.betBuilderDialogService.closeDialog()),
                ignoreElementsTyped(),
            );
        },
        {
            dispatch: false,
        },
    );

    preCreatedBAB$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.requestReplacePreCreatedBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            concatMap(([action, betslipState]) => {
                const precreatedPick = betslipState.picks.pickList.find(
                    (pick) => BetslipBetBuilderPick.isPick(pick) && pick.id === action.pickId,
                ) as BetslipBetBuilderPick;

                const newPick = this.pickFactory.convertPreCreatedBABPick(precreatedPick);

                return of(BetslipPickActions.replacePick({ pickId: action.pickId, newPick })).pipe(
                    finalize(() => {
                        this.betBuilderUtilService.navigateToBABTab(newPick, action.headerTemplate);
                    }),
                );
            }),
        );
    });

    handleSwitchToComboWhenAddingBetBuilderPick$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            filter(() => effect.betslipConfig.isSportcastAsComboEnabled),
            ofType(BetslipPickActions.requestAddEntainUiBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipCurrentTypeSelector)),
            filter(([_, currentType]) => currentType === BetslipType.Single || currentType === BetslipType.System),
            map(() =>
                BetslipTypesActions.setSelectedType({
                    betslipType: BetslipType.Combo,
                    selectedByUser: false,
                    systemInfo: null,
                }),
            ),
        );
    });

    private getPickStake(typeState: IBetslipTypeState, pickId: string): number {
        return (this.betslipConfig.isSportcastAsComboEnabled ? typeState.comboBet.stake : typeState.singleBet.picks[pickId].stake) ?? 0;
    }
}
